import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Menu, Preloader, TopNav } from "@/components";
import {
  useCreateTrainingMutation,
  useGetExercizesMutation,
  useRemoveExercizeFromTrainingMutation,
} from "@/store/trainingsApi";
import { AddExerciseModal } from "@/features/add-exercise-modal";
import { EditExerciseModal } from "@/features/edit-exercise-modal";
import { formatScheduleDate } from "@/shared/utils/";
import { tg } from "@/constants/telegram";
import { User } from "@/entities/user";
import toast from "react-hot-toast";
import { Training } from "@/components/Trains/model/types";
import styles from "./EditTrainingPage.module.scss";

type EditTrainingPageProps = {
  initData: string;
  user: User;
  fromUser?: number;
  isAdmin?: boolean;
  backPath?: string;
};

export const EditTrainingPage: React.FC<EditTrainingPageProps> = (props) => {
  const { initData, user, fromUser, isAdmin, backPath } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [training, setTraining] = useState<Training | null>(null);
  const [exercizes, setExercizes] = useState<any[]>([]);
  const [showAddExerciseModal, setShowAddExerciseModal] = useState(false);
  const [showEditExerciseModal, setShowEditExerciseModal] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState<number | null>(null);
  const [editingExercise, setEditingExercise] = useState<any | null>(null);

  const [createTraining] = useCreateTrainingMutation();
  const [getExercizes] = useGetExercizesMutation();
  const [removeExercize] = useRemoveExercizeFromTrainingMutation();

  useEffect(() => {
    tg.ready();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const [trainingResponse] = await Promise.all([
          createTraining({ fromUser }).unwrap(),
        ]);

        const trainingsData = trainingResponse.trainings || {};
        const allTrainings = [
          ...(Array.isArray(trainingsData.created_by_user) ? trainingsData.created_by_user : []),
          ...(Array.isArray(trainingsData.schedule) ? trainingsData.schedule : [])
        ];

        const parsedId = parseInt(id);
        const selectedTraining = allTrainings.find(
          (item: Training) => item.id === parsedId
        );

        if (!selectedTraining) {
          throw new Error("Training not found");
        }

        // Для всех тренировок используем training_type: "schedule"
        const exercisesResponse = await getExercizes({
          training_id: Number(id),
          training_type: "schedule" as "free" | "schedule",
          fromUser: fromUser,
        }).unwrap();

        setTraining(selectedTraining);
        const loadedExercises = Array.isArray(exercisesResponse.exercizes) 
          ? exercisesResponse.exercizes 
          : [];
        setExercizes(loadedExercises);

        // Если тренировка пустая, автоматически открываем модальное окно добавления
        if (loadedExercises.length === 0) {
          setTimeout(() => {
            setShowAddExerciseModal(true);
          }, 500);
        }
      } catch (error: any) {
        console.error("Ошибка загрузки тренировки:", error);
        toast.error("Ошибка загрузки тренировки");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, getExercizes, createTraining]);

  const handleRefreshExercises = async () => {
    if (!id) return;
    
    try {
      const exercisesResponse = await getExercizes({
        training_id: Number(id),
        training_type: "schedule" as "free" | "schedule",
        fromUser: fromUser,
      }).unwrap();
      setExercizes(exercisesResponse.exercizes || []);
    } catch (error) {
      console.error("Ошибка обновления упражнений:", error);
    }
  };

  const handleRemoveExercise = async (exerciseId: number) => {
    if (!id) return;
    
    try {
      await removeExercize({
        training_id: Number(id),
        exercize_id: exerciseId,
        fromUser: fromUser,
      }).unwrap();
      toast.success("Упражнение удалено");
      await handleRefreshExercises();
    } catch (error: any) {
      console.error("Ошибка удаления упражнения:", error);
      toast.error(error?.data?.detail || "Ошибка при удалении упражнения");
    }
  };

  const handleEditExercise = (exerciseId: number) => {
    const exercise = exercizes?.find((ex) => ex.id === exerciseId);
    if (exercise) {
      setEditingExercise(exercise);
      setEditingExerciseId(exerciseId);
      setShowEditExerciseModal(true);
    }
  };

  if (isLoading) {
    return (
      <>
        <Preloader hasMenu />
        <Menu active={0} />
      </>
    );
  }

  if (!training) {
    return (
      <div>
        <Menu active={0} />
      </div>
    );
  }

  const isEmptyTraining = exercizes.length === 0;

  return (
    <div className={styles.root}>
      <div className="container" style={{ position: "relative" }}>
        <TopNav
          linkPath={backPath || (isAdmin && fromUser ? `/admin/chats/${fromUser}/trainings-program` : "/trains")}
          handleSetActivePage={() => null}
          title="Редактирование тренировки"
        />

        <div className={styles.header}>
          <div className={styles.dateBadge}>
            {formatScheduleDate(training.schedule)}
          </div>
          <h1 className={styles.title}>{training.title}</h1>
        </div>

        <div className={styles.content}>
          {isEmptyTraining ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📝</div>
              <p className={styles.emptyText}>
                Тренировка пустая. Добавьте упражнения для начала.
              </p>
              <button
                className={styles.addButton}
                onClick={() => setShowAddExerciseModal(true)}
              >
                <span className={styles.addButtonIcon}>+</span>
                Добавить упражнение
              </button>
            </div>
          ) : (
            <>
              <div className={styles.exercisesList}>
                {exercizes.map((exercise, index) => (
                  <div key={exercise.id} className={styles.exerciseCard}>
                    <div className={styles.exerciseContent}>
                      {exercise.title_photo && (
                        <img
                          src={exercise.title_photo}
                          alt={exercise.title}
                          className={styles.exerciseImage}
                        />
                      )}
                      <div className={styles.exerciseInfo}>
                        <h3 className={styles.exerciseTitle}>{exercise.title}</h3>
                        {exercise.muscle_group && (
                          <span className={styles.exerciseMuscleGroup}>
                            {exercise.muscle_group}
                          </span>
                        )}
                        {exercise.config && exercise.config.length > 0 && (
                          <div className={styles.exerciseApproaches}>
                            {exercise.config.map((approach: any, idx: number) => (
                              <span key={idx} className={styles.approachBadge}>
                                {approach.repetitions} повторений
                                {approach.weight && ` × ${approach.weight} кг`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.exerciseActions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditExercise(exercise.id)}
                        title="Редактировать"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path
                            d="M12.5 2.5L15.5 5.5M13.5 1.5L16.5 4.5L9.5 11.5H6.5V8.5L13.5 1.5Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleRemoveExercise(exercise.id)}
                        title="Удалить"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path
                            d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className={styles.addButton}
                onClick={() => setShowAddExerciseModal(true)}
              >
                <span className={styles.addButtonIcon}>+</span>
                Добавить упражнение
              </button>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelButton}
            onClick={() => {
              if (isAdmin && fromUser) {
                navigate(`/admin/chats/${fromUser}/trainings-program`);
              } else {
                navigate(`/workout-preview/${id}`);
              }
            }}
          >
            Отмена
          </button>
          <button
            className={styles.saveButton}
            onClick={() => {
              toast.success("Тренировка сохранена");
              if (isAdmin && fromUser) {
                navigate(`/admin/chats/${fromUser}/trainings-program`);
              } else {
                navigate(`/workout-preview/${id}`);
              }
            }}
          >
            Сохранить
          </button>
        </div>
      </div>

      {showAddExerciseModal && (
        <AddExerciseModal
          onClose={() => setShowAddExerciseModal(false)}
          initData={initData}
          trainingId={Number(id)}
          onSuccess={handleRefreshExercises}
          fromUser={fromUser}
        />
      )}

      {showEditExerciseModal && editingExercise && editingExerciseId && (
        <EditExerciseModal
          onClose={() => {
            setShowEditExerciseModal(false);
            setEditingExercise(null);
            setEditingExerciseId(null);
          }}
          initData={initData}
          trainingId={Number(id)}
          exerciseId={editingExerciseId}
          currentExercise={{
            id: editingExercise.id,
            title: editingExercise.title,
            title_photo: editingExercise.title_photo,
            muscle_group: editingExercise.muscle_group,
          }}
          currentApproaches={
            editingExercise.config && editingExercise.config.length > 0
              ? editingExercise.config.map((cfg: any) => ({
                  approach_number: cfg.approach_num || 1,
                  repetitions: cfg.repetitions || 10,
                  weight: cfg.weight?.toString() || "",
                  repetitions_margin: cfg.repetition_margin || undefined,
                }))
              : [
                  {
                    approach_number: 1,
                    repetitions: editingExercise.repetitions || 10,
                    weight: editingExercise.weight?.toString() || "",
                  },
                ]
          }
          onSuccess={handleRefreshExercises}
          fromUser={fromUser}
        />
      )}

      <Menu active={0} />
    </div>
  );
};


